import { useState, useEffect, useCallback } from 'react'

type StorageType = 'local' | 'session'

export function useStorage<T>(
  key: string,
  defaultValue: T,
  type: StorageType = 'local'
): { value: T; setValue: (val: T) => void; remove: () => void } {
  const storage =
    typeof window !== 'undefined'
      ? type === 'local'
        ? window.localStorage
        : window.sessionStorage
      : null

  const [value, setValueState] = useState<T>(() => {
    if (!storage) return defaultValue
    try {
      const item = storage.getItem(key)
      return item ? (JSON.parse(item) as T) : defaultValue
    } catch {
      return defaultValue
    }
  })

  const setValue = useCallback(
    (val: T) => {
      setValueState(val)
      if (storage) {
        storage.setItem(key, JSON.stringify(val))
      }
    },
    [key, storage]
  )

  const remove = useCallback(() => {
    setValueState(defaultValue)
    if (storage) {
      storage.removeItem(key)
    }
  }, [key, storage])

  useEffect(() => {
    const onStorageChange = (event: StorageEvent) => {
      if (event.key === key) {
        try {
          setValueState(event.newValue ? JSON.parse(event.newValue) : defaultValue)
        } catch {
          setValueState(defaultValue)
        }
      }
    }
    window.addEventListener('storage', onStorageChange)
    return () => window.removeEventListener('storage', onStorageChange)
  }, [key, defaultValue, storage])

  return { value, setValue, remove }
}
